// Role-based access control (§4). Permission checks are enforced on every
// route (API) and every screen/export (web).

export const ROLES = [
  'admin',
  'estimator',
  'salesperson',
  'project_manager',
  'executive',
  'viewer',
  'supplier_view',
] as const;

export type Role = (typeof ROLES)[number];

export const ROLE_LABELS: Record<Role, string> = {
  admin: 'Admin',
  estimator: 'Estimator',
  salesperson: 'Salesperson',
  project_manager: 'Project Manager',
  executive: 'Executive',
  viewer: 'Viewer',
  supplier_view: 'Supplier (export only)',
};

// Capability flags. Phase 1 uses a subset; later phases extend this map.
export type Capability =
  | 'project.create'
  | 'project.view'
  | 'project.edit'
  | 'count.edit'
  | 'bom.edit'
  | 'bom.viewInternal'   // internal labor/pricing visibility
  | 'estimate.view'
  | 'estimate.approve'
  | 'proposal.generate'
  | 'rfi.manage'
  | 'co.manage'
  | 'pricing.manage'
  | 'labor.manage'
  | 'wage.manage'
  | 'intel.view'
  | 'admin.settings'
  | 'supplier.export';

const ALL: Capability[] = [
  'project.create', 'project.view', 'project.edit', 'count.edit', 'bom.edit',
  'bom.viewInternal', 'estimate.view', 'estimate.approve', 'proposal.generate',
  'rfi.manage', 'co.manage', 'pricing.manage', 'labor.manage', 'wage.manage',
  'intel.view', 'admin.settings', 'supplier.export',
];

export const ROLE_CAPS: Record<Role, Capability[]> = {
  admin: ALL,
  estimator: [
    'project.create', 'project.view', 'project.edit', 'count.edit', 'bom.edit',
    'bom.viewInternal', 'estimate.view', 'rfi.manage', 'co.manage',
    'pricing.manage', 'labor.manage', 'wage.manage', 'supplier.export',
  ],
  salesperson: ['project.view', 'proposal.generate'], // NO internal BOM/pricing unless granted
  project_manager: [
    'project.view', 'estimate.view', 'rfi.manage', 'co.manage', 'intel.view',
  ],
  executive: ['project.view', 'estimate.view', 'intel.view'],
  viewer: ['project.view'],
  supplier_view: ['supplier.export'],
};

export function can(role: Role, cap: Capability): boolean {
  return ROLE_CAPS[role]?.includes(cap) ?? false;
}
